import {
  ListingType,
  MLSListing,
  PropertyData,
  mlsInfoByOUID,
  schFilledAddress,
  toUADListingStatus,
} from '@aloft/toolkit-shared';

import { useAutofillProperty } from '../../../hooks/useAutofillProperty.ts';
import { normalizeRentalListingStatus } from './normalizeRentalListingStatus.ts';

const toTransactionStatus = (
  status: string | undefined,
): PropertyData['editableData']['transaction']['status'] | undefined => {
  const uadStatus = toUADListingStatus(status);
  if (uadStatus) return uadStatus;

  const rentalStatus = normalizeRentalListingStatus(status);
  if (rentalStatus === 'Active') return 'Active';
  if (rentalStatus === 'Pending') return 'Contract';
  if (rentalStatus === 'Rented') return 'SettledSale';

  return undefined;
};

export const listingToPropertyData =
  (
    autofillProperty: ReturnType<typeof useAutofillProperty>,
    effectiveYear: number,
    orderID: string,
    listingType?: ListingType,
  ) =>
  (listingData: MLSListing): Promise<PropertyData> => {
    const address = schFilledAddress.safeParse({
      AddressLineText: listingData.UnparsedAddress ?? undefined,
      CityName: listingData.City ?? undefined,
      PostalCode: listingData.PostalCode ?? undefined,
      StateCode: listingData.StateOrProvince ?? undefined,
      CountyName: listingData.CountyOrParish ?? undefined,
    });
    const mlsInfo = mlsInfoByOUID(listingData.MLSOUID);

    const mlsListingInfo =
      mlsInfo?.mlsid && listingData.ListingId
        ? {
            mlsListingID: listingData.ListingId,
            mls: mlsInfo?.mlsid,
          }
        : undefined;

    if (address.success === false) {
      throw new Error(
        `Listing must have valid address details to convert to PropertyData: ${JSON.stringify(
          listingData,
        )}`,
      );
    }

    return autofillProperty({
      address: address.data,
      effectiveYear,
      orderID,
      mlsListingInfo,
      listingType,
    }).then((propertyData) => ({
      ...propertyData,
      editableData: {
        ...propertyData.editableData,
        transaction: {
          ...propertyData.editableData.transaction,
          status:
            propertyData.editableData.transaction.status ??
            toTransactionStatus(listingData.MlsStatus),
          rentalListingStatus:
            propertyData.editableData.transaction.rentalListingStatus ??
            normalizeRentalListingStatus(listingData.MlsStatus),
        },
      },
    }));
  };
